<template>
  <div class="page-manage">
    <div class="download-excel-btn">
      <el-tooltip
        content="엑셀 다운로드"
        placement="top"
        effect="light"
      >
        <el-button
          type="primary"
          size="large"
          aria-label="엑셀 다운로드"
          @click="downloadExcel"
          class="btn-icon"
        >
          <font-awesome-icon icon="file-excel" />
        </el-button>
      </el-tooltip>
    </div>
    <div class="search-area">
      <el-input
        v-model="searchKeyword"
        placeholder="뷰저블 계정을 입력하세요."
        clearable
        @clear="clearSearch"
        size="large"
        style="width: 300px"
        class="input-search"
        @keyup.enter="searchKeyword.length >= 2 ? handleSearch() : null"
      >
        <template #append>
          <el-button
            aria-label="검색"
            @click="handleSearch"
          >
            <font-awesome-icon icon="fa-solid fa-magnifying-glass" />
          </el-button>
        </template>
      </el-input>
      <el-button
        type="info"
        size="large"
        class="btn-reset"
        @click="handleReset"
      >
        <font-awesome-icon icon="fa-solid fa-rotate-left" />
      </el-button>
    </div>
    <template v-if="!loading && !isError">
      <el-table
        :data="data"
        :default-sort="{ prop: 'id', order: 'descending' }"
        style="width: 100%"
        ref="tableRef"
        @sort-change="handleSortChange"
        @filter-change="handleFilterChange"
      >
        <el-table-column
          label="자세히"
          width="60"
          align="center"
          fixed
        >
          <template #default="scope">
            <el-button
              size="small"
              aria-label="자세히"
              @click="toRequestView(scope.row.requestId)"
            >
              <font-awesome-icon icon="fa-regular fa-file-lines" />
            </el-button>
          </template>
        </el-table-column>
        <el-table-column
          prop="state"
          label="상태"
          width="120"
          align="center"
          fixed
          column-key="state"
          :filters="[
            { text: '플랜 생성 대기', value: 'CREATED_REQUEST_PLAN' },
            { text: 'PV 충전 대기', value: 'CREATED_REQUEST_PV' },
            { text: 'Jam 충전 대기', value: 'CREATED_REQUEST_JAM' },
            { text: '부가 서비스 처리 대기', value: 'CREATED_REQUEST_ADDITIONAL' },
            { text: '무통장 입금 대기', value: 'DAY_PAYMENT_WIRE' },
            { text: '무통장 입금 지연', value: 'DAY_PAYMENT_WIRE_DELAY' },
            { text: '카드 결제 대기', value: 'DAY_PAYMENT_CARD' },
            { text: '세금 계산서 발행', value: 'DAY_INVOICE_TAX' },
            { text: '서류 발행', value: 'DAY_INVOICE' },
            { text: '플랜 연장 필요', value: 'NEED_TO_EXTEND' },
            { text: '이슈 없음', value: 'NON_ISSUE' },
            { text: '리퀘스트 종료', value: 'COMPLETED_REQUEST' },
          ]"
        >
          <template #default="scope">
            <status-tag 
              v-for="(stat, index) in scope.row.state" 
              :key="index"
              :color="getTagColor(stat)"
              size="small"
              :text="getRequestStatusTypeValue(stat)"
            />
          </template>
        </el-table-column>
        <el-table-column
          prop="companyName"
          label="회사명 / 계정"
          fixed
          width="220"
          sortable="custom"
        >
          <template #default="scope">
            {{ scope.row.companyName }}<br/>
            {{ scope.row.userId }}
          </template>
        </el-table-column>
        <el-table-column
          prop="requestType"
          label="요청 유형"
          width="100"
          align="center"
          column-key="requestType"
          :filters="[
            { text: '플랜', value: 'PLAN' },
            { text: 'PV 충전', value: 'PV_CHARGE' },
            { text: 'Jam 충전', value: 'JAM_CHARGE' },
            { text: '부가 서비스', value: 'ADDITIONAL' }
          ]"
        >
          <template #default="scope">
            <status-tag 
              :color="getTagColor(scope.row.requestType)"
              size="small"
              :text="getRequestTypeValue(scope.row.requestType)"
            />
          </template>
        </el-table-column>
        <el-table-column
          label="플랜명"
          width="140"
        >
          <template #default="scope">
            <span 
              v-if="!scope.row.beusPlanNumber && !scope.row.baPlanNumber"
              class="center"
            >
              -
            </span>
            <span 
              v-if="scope.row.beusPlanNumber"
              class="plan"
            >
              <img 
                :src="IconBeusGray" 
                alt="히트맵 플랜"
              />
                {{  getBeusPlanLName(Number(scope.row.beusPlanNumber)) }}
            </span>
            <span 
              v-if="scope.row.baPlanNumber"
              class="plan"
            >
              <img 
                :src="IconBiGray" 
                alt="저니맵 플랜"
              />
                {{  getBaPlanLName(Number(scope.row.baPlanNumber)) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          prop="useStartDate"
          label="사용 시작일"
          width="110"
          align="center"
          sortable="custom"
        />
        <el-table-column
          prop="useEndDate"
          label="사용 종료일"
          width="110"
          align="center"
          sortable="custom"
        >
          <template #default="scope">
            {{ scope.row.useEndDate ? scope.row.useEndDate : '-'}}
          </template>
        </el-table-column>
        <el-table-column
            prop="period"
            label="사용 기간"
            width="100"
            align="center"
            sortable="custom"
          >
            <template #default="scope">
              <status-tag
                v-if="scope.row.period"
                color="gray"
                size="small"
                :text="`${scope.row.period}개월`"
              />
              <template v-else>
                -
              </template>
            </template>
          </el-table-column>
          <el-table-column
            prop="recurring"
            label="자동 갱신"
            width="100"
            align="center"
            column-key="recurring"
            :filters="[
              { text: 'Yes', value: true },
              { text: 'No', value: false }
            ]"
          >
            <template #default="scope">
              <status-tag
                :color="scope.row.recurring ? 'blue': 'gray'"
                size="small"
                :text="scope.row.recurring ? 'Yes' : 'No'"
              />
            </template>
          </el-table-column>
          <el-table-column
            prop="paymentType"
            label="결제 방식"
            width="100"
            align="center"
            column-key="paymentType"
            :filters="[
              { text: '무통장', value: 'wire' },
              { text: '카드', value: 'card' },
              { text: '무상', value: 'free' }
            ]"
          >
            <template #default="scope">
              <status-tag 
                :color="getTagColor(scope.row.paymentType)"
                size="small"
                :text="getPaymentTypeValue(scope.row.paymentType)"
              />
            </template>
          </el-table-column>
          <el-table-column
            prop="amount"
            label="총 결제 금액"
            width="110"
            align="right"
            sortable="custom"
          >
            <template #default="scope">
              {{ new Intl.NumberFormat().format(scope.row.amount || 0) }} 원
            </template>
          </el-table-column>
          <el-table-column
            prop="paymentDate"
            label="최초 결제일"
            width="110"
            align="center"
            sortable="custom"
          />
          <el-table-column
            prop="paymentCycle"
            label="결제 주기"
            width="110"
            align="center"
            column-key="paymentCycle"
            :filters="[
              { text: '일시납', value: 0 },
              { text: '월납', value: 1 },
              { text: '격월납', value: 2 },
              { text: '분기납', value: 3 },
              { text: '4개월납', value: 4 },
              { text: '반기납', value: 6 },
              { text: '연납', value: 12 }
            ]"
          >
            <template #default="scope">
              <status-tag 
                :color="getTagColor(String(scope.row.paymentCycle))"
                size="small"
                :text="scope.row.paymentCycle ? getPaymentCycleTypeValue(scope.row.paymentCycle) : getPaymentCycleTypeValue(0)"
              />
          </template>
          </el-table-column>
          <el-table-column
            prop="docList"
            label="발행 서류"
            width="140"
            align="center"
            column-key="docList"
            :filters="[
              { text: '세금계산서', value: 'TAX_INVOICE' },
              { text: '견적서', value: 'QUOTATION' },
              { text: '계약서', value: 'CONTRACT' },
              { text: '청구서', value: 'INVOICE' },
              { text: '거래명세서', value: 'TRANSACTION' },
              { text: '정산요청서', value: 'SETTLEMENT' }
            ]"
          >
            <template #default="scope">
              <span 
                v-for="(doc, index) in uniqueDocTypes(scope.row.docList)"
                :key="index"
                class="center"
              >
                {{ getDocumentTypeValue(doc) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column
            prop="memo"
            label="메모"
            width="300"
          />
      </el-table>
      <div class="pagination-area">
        <div class="count">
          총 <strong>{{ totalCount }}</strong>개의 리퀘스트
        </div>
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[30, 50, 100]"
          :total="totalCount"
          layout="sizes, prev, pager, next"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </template>
    <template v-else-if="loading">
      <common-loader/>
    </template>
    <template v-else-if="isError && !loading">
      <CommonError message="서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.<br/> 문제가 지속되면 관리자에게 문의하세요."/>
    </template>
  </div>
</template>

<script lang="ts" setup>
import {ref, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { getRequestList } from "@/api";
import { getRequestTypeValue, getRequestStatusTypeValue, getPaymentTypeValue, getPaymentCycleTypeValue, getDocumentTypeValue } from "@/utils/getValueForKey";
import { getBeusPlanLName, getBaPlanLName, getTagColor } from "@/utils";
import { createExcel } from "@/utils/createExcel"
import  { DocumentTypeKeys }  from "@/types/constants";
import { Request } from '@/types/Request';
import StatusTag from '@/components/StatusTag.vue';
import type { TableInstance } from 'element-plus';
import IconBeusGray from '@/assets/images/ico-beus-g.svg';
import IconBiGray from '@/assets/images/ico-bi-g.svg';
import { ElMessageBox } from 'element-plus';
import CommonError from '@/components/CommonError.vue';
import CommonLoader from '@/components/CommonLoader.vue';

const router = useRouter();
const route = useRoute();

const data = ref<Request[]>([]);
const totalCount = ref<number>(0);
const currentPage = ref<number>(1);
const pageSize = ref<number>(100);
const loading = ref<boolean>(false);
const isError = ref<boolean>(false);
const searchKeyword = ref('');
const clearSearch = () => {
  searchKeyword.value = '';
};
const sortValue = ref<string | undefined>(undefined);
const selectedFilters = ref<{ [key: string]: string[] } | undefined>({});

const fetchData = async () => {
  loading.value = true;
  const params: Record<string, any> = {
    pageNum: currentPage.value,
    pageSize: pageSize.value,
  }

  if (searchKeyword.value.length >= 2) {
    params.userId = searchKeyword.value;
  }

  if (sortValue.value) {
    params.sort = sortValue.value;
  }

  if (selectedFilters.value) {
    const searchFilters: Record<string, any> = {};
    Object.entries(selectedFilters.value).forEach(([key, value]) => {
      if (value.length > 0) {
        searchFilters[key] = value;
      }
    });

    if (Object.keys(searchFilters).length > 0) {
      params.search = searchFilters;
    }
  }


  try {
    const response = await getRequestList(params);
    if(response.success) {
      // console.log(response.result);
      totalCount.value = response.result.count;
      data.value = response.result.list;
    }
  } catch (error) {
    loading.value = false;
    isError.value = true;
    console.error('Error Message:', error);
  } finally {
    loading.value = false;
  }
}

watch(
  () => route.query.param,
  (newParam) => {
    if (newParam) {
      searchKeyword.value = newParam as string;
      fetchData();
    } else {
      searchKeyword.value = '';
      fetchData();
    }
  },
  { immediate: true }
);

const handleSearch = async () =>  {
  if(searchKeyword.value.length < 2) {
    ElMessageBox.alert('검색어는 2자 이상 입력해주세요.', {
      confirmButtonText: '확인',
    });
    return;
  }
  router.replace({ path: '/request/manage', query: { param: searchKeyword.value } });
}

const toRequestView = (id: string) => router.push(`/request/view/${id}`)

const handleSizeChange = (size: number) => {
  pageSize.value = size;
  fetchData();
}
const handlePageChange = (page: number) => {
  currentPage.value = page;
  fetchData();
};

const handleSortChange = ({ prop, order }: { prop: string; order: string }) => {
  if (order === "ascending") {
    sortValue.value = prop;
  } else if (order === "descending") {
    sortValue.value = `-${prop}`
  } else {
    sortValue.value = undefined;
  }
  fetchData();
};

const handleFilterChange = (filters: Record<string, string[]>) => {
  selectedFilters.value = filters || [];
  fetchData();
};

const tableRef = ref<TableInstance>()

const handleReset = () => {
  searchKeyword.value = '';
  currentPage.value = 1;
  pageSize.value = 30;
  tableRef.value?.clearFilter();
  tableRef.value?.clearSort();
  sortValue.value = undefined;
  selectedFilters.value = undefined;
  router.replace({ path: '/request/manage' });
  fetchData(); 
}

const uniqueDocTypes = (docList: DocumentTypeKeys[]) => Array.from(new Set(docList))

const downloadExcel = async () => {
  const params: Record<string, any> = {
    pageNum: currentPage.value,
    pageSize: totalCount.value,
  }

  try {
    const response = await getRequestList(params);
    if(response.success) {
      // console.log(response.result);
      createExcel(response.result.list);
    }
  } catch (error) {
    loading.value = false;
    isError.value = true;
    console.error('Error Message:', error);
  }
}
</script>